// src/services/openaiService.js
import essayService from './essayService.js';

class OpenAIService {
  constructor() {
    this._mock = false;
    this._cache = new Map(); // Simple in-memory cache
    this._cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
    this._cacheHits = 0;
    this._cacheMisses = 0;

    const envKey =
      (typeof process !== 'undefined' && process.env
        ? process.env.REACT_APP_OPENAI_API_KEY || process.env.OPENAI_API_KEY
        : null) || null;

    this._envApiKey = typeof envKey === 'string' && envKey.trim().length > 0
      ? envKey.trim()
      : null;
    this._apiKey = null;
  }

  setMockMode(v) { this._mock = !!v; }
  get mockMode() { return this._mock; }

  get effectiveApiKey() {
    return this._apiKey || this._envApiKey || null;
  }

  get shouldAskKey() {
    // If the environment already provides a key, we don't need to prompt the user.
    return !this._envApiKey;
  }

  setApiKey(key) {
    if (typeof key === 'string' && key.trim()) {
      this._apiKey = key.trim();
    } else {
      this._apiKey = null;
    }
  }

  isValidApiKey(key) {
    if (typeof key !== 'string') return false;
    const trimmed = key.trim();
    if (!trimmed) return false;
    // Basic OpenAI pattern (sk- prefix) to avoid leaking invalid requests.
    if (trimmed.startsWith('sk-') && trimmed.length >= 40) return true;
    // Allow developer-provided keys for testing (e.g., sk-proj-*)
    return /^sk-[A-Za-z0-9]{20,}$/.test(trimmed) || /^sk-proj-[A-Za-z0-9_-]{20,}$/.test(trimmed);
  }

  async testApiKey(key) {
    if (!this.isValidApiKey(key)) return false;

    const previous = this._apiKey;
    try {
      this._apiKey = key.trim();
      const res = await fetch('https://api.openai.com/v1/models', {
        headers: { Authorization: `Bearer ${this._apiKey}` }
      });
      return res.ok;
    } catch (err) {
      console.warn('Failed to validate OpenAI API key:', err);
      // OpenAI blocks browser requests via CORS; treat network errors as inconclusive.
      return true;
    } finally {
      this._apiKey = previous;
    }
  }

  // Generate cache key for similar essays
  _getCacheKey(text, isImage = false) {
    if (isImage) return null; // Don't cache image analyses
    
    // Normalize text and create a simple hash-like key
    const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
    const words = normalized.split(' ');
    
    // Create key based on length, word count, and first/last words
    return `${words.length}_${normalized.length}_${words[0]}_${words[words.length-1]}`;
  }

  // Check cache before API call
  _getFromCache(key) {
    if (!key) return null;
    const cached = this._cache.get(key);
    if (cached && Date.now() - cached.timestamp < this._cacheExpiry) {
      this._cacheHits += 1;
      return cached.data;
    }
    if (cached) this._cache.delete(key); // Remove expired
    this._cacheMisses += 1;
    return null;
  }

  // Store in cache
  _setCache(key, data) {
    if (!key) return;
    this._cache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    // Simple cache size management
    if (this._cache.size > 100) {
      const oldestKey = this._cache.keys().next().value;
      this._cache.delete(oldestKey);
    }
  }

  async analyzeEssay(plainText, setProgress = () => {}, imageB64 = null, imageUrl = null, examType = 'enem', themeName = null) {
    if (this._mock) {
      setProgress('Analisando (mock)…');
      await sleep(400);
      return {
        analysis: JSON.stringify(this._mockResponse(plainText, !!imageB64)),
        usage: { prompt_tokens: 100, completion_tokens: 300, total_tokens: 400 },
        timestamp: Date.now(),
        isImageAnalysis: !!imageB64
      };
    }

    // Check cache first (only for text)
    const cacheKey = this._getCacheKey(plainText, !!imageB64);
    const cached = this._getFromCache(cacheKey);
    if (cached) {
      setProgress('Análise encontrada no cache…');
      return cached;
    }

    setProgress('Enviando para o servidor…');

    const clientSuppliedKey = !this._envApiKey && this._apiKey ? this._apiKey : null;

    const payload = {
      text: plainText || '',
      imageUrl: imageUrl || null,      // preferred
      imageBase64: imageB64 || null,
      examType: examType || 'enem',    // NEW: exam type
      theme: themeName || null,
      clientApiKey: clientSuppliedKey || undefined
    };
    // Enhanced retry with exponential backoff
    const attempts = [0, 1000, 3000, 8000]; // Progressive backoff
    let lastErr;

    for (let i = 0; i < attempts.length; i++) {
      if (i > 0) {
        setProgress(`Tentativa ${i + 1}… aguardando ${attempts[i]}ms`);
        await sleep(attempts[i]);
      }

      try {
        const resp = await fetch('/api/analyze-essay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const text = await resp.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          const preview = text.slice(0, 180).replace(/\s+/g, ' ');
          throw new Error(`Bad response (${resp.status}). Body: ${preview}`);
        }

        if (!resp.ok) {
          const code = json?.error?.code || json?.error || 'Server error';
          throw new Error(`API ${resp.status}: ${typeof code === 'string' ? code : JSON.stringify(json)}`);
        }

        // Cache successful result
        this._setCache(cacheKey, json);
        return json;

      } catch (e) {
        lastErr = e;
        const msg = String(e?.message || '');
        const retryable =
          msg.includes('504') || msg.includes('502') || msg.includes('429') ||
          msg.includes('timeout') || msg.includes('AbortError') ||
          msg.includes('ECONNRESET') || msg.includes('ETIMEDOUT');

        if (!retryable || i === attempts.length - 1) break;
      }
    }

    throw lastErr || new Error('Analyze request failed after all retries');
  }

  parseAnalysisResponse(raw) {
    if (!raw) return {};
    try { 
      return JSON.parse(raw); 
    } catch {}

    // Enhanced JSON extraction with better patterns
    const patterns = [
      /```json\s*([\s\S]*?)\s*```/i,
      /```\s*([\s\S]*?)\s*```/i,
      /\{[\s\S]*\}/i // Last resort: find any object-like structure
    ];

    for (const pattern of patterns) {
      const match = String(raw).match(pattern);
      if (match) {
        try {
          return JSON.parse(match[1] || match[0]);
        } catch {}
      }
    }
    return {};
  }

  getUsageStats(u = {}) {
    const promptTokens = +u.prompt_tokens || 0;
    const completionTokens = +u.completion_tokens || 0;
    const total = +u.total_tokens || (promptTokens + completionTokens);
    
    // Current GPT-4o-mini pricing (as of 2025)
    const inputCostPer1M = 0.15; // $0.15 per 1M input tokens
    const outputCostPer1M = 0.60; // $0.60 per 1M output tokens
    
    const estimatedCost = 
      (promptTokens / 1_000_000 * inputCostPer1M) + 
      (completionTokens / 1_000_000 * outputCostPer1M);
    
    return { 
      ...u, 
      totalTokens: total, 
      estimatedCost: estimatedCost.toFixed(6),
      breakdown: {
        inputTokens: promptTokens,
        outputTokens: completionTokens,
        inputCost: (promptTokens / 1_000_000 * inputCostPer1M).toFixed(6),
        outputCost: (completionTokens / 1_000_000 * outputCostPer1M).toFixed(6)
      }
    };
  }

  // Clear cache manually if needed
  clearCache() {
    this._cache.clear();
    this._cacheHits = 0;
    this._cacheMisses = 0;
  }

  // Get cache stats for debugging
  getCacheStats() {
    return {
      size: this._cache.size,
      hitRate: this._cacheHits / (this._cacheHits + this._cacheMisses) || 0,
      totalRequests: this._cacheHits + this._cacheMisses
    };
  }

  _mockResponse(text) {
    const score = Math.max(300, Math.min(950, Math.round((text || '').length / 4)));
    const comp = (s, j) => ({ score: s, justification: j });
    return {
      totalScore: score,
      scores: {
        I: comp(160, 'Domínio adequado da norma culta.'),
        II: comp(140, 'Compreensão parcial do tema.'),
        III: comp(150, 'Organização textual satisfatória.'),
        IV: comp(130, 'Coesão e coerência variáveis.'),
        V: comp(140, 'Proposta de intervenção presente.')
      },
      inlineErrors: [
        { 
          start: 10, 
          end: 18, 
          type: 'grammar', 
          originalText: 'foi feito', 
          correction: 'foram feitos', 
          explanation: 'Concordância verbal.' 
        },
        { 
          start: 60, 
          end: 68, 
          type: 'punctuation', 
          originalText: 'porem', 
          correction: 'porém', 
          explanation: 'Acentuação e vírgula.' 
        }
      ],
      problems: ['Uso inconsistente de conectivos.', 'Argumentos pouco aprofundados.'],
      suggestions: ['Acrescente dados e exemplos.', 'Revise a concordância e a pontuação.'],
      feedbackSummary: 'Boa estrutura geral, mas falta aprofundamento e revisão linguística.',
      penaltyTable: [
        { type: 'spelling', description: 'Repetição de erro ortográfico (ex.: nao)', snippet: '... nao ...', points: 40 },
        { type: 'grammar', description: 'Repetição de conjugação incorreta (estar/está)', snippet: 'A sociedade estar...', points: 40 }
      ],
      paragraphFeedback: [
        { paragraph: 1, summary: 'Introdução apresenta tese com repertório simples.', strength: 'Tese clara.', improvement: 'Inclua repertório legitimado para fortalecer o gancho inicial.', snippet: 'A sociedade contemporânea enfrenta...' },
        { paragraph: 2, summary: 'Primeiro desenvolvimento traz exemplo sem dados.', strength: 'Conexão com o tema mantida.', improvement: 'Traga dado ou citação para sustentar o argumento.', snippet: 'Um exemplo disso é que muitas pessoas...' },
        { paragraph: 3, summary: 'Segundo desenvolvimento repete ideia anterior.', strength: 'Uso de conectivos adequados.', improvement: 'Introduza novo ponto de vista ou consequência prática.', snippet: 'Além disso, a falta de informação...' },
        { paragraph: 4, summary: 'Conclusão retoma a tese e propõe intervenção.', strength: 'Estrutura de proposta presente.', improvement: 'Detalhe agente/meio/efeito para a intervenção.', snippet: 'Portanto, é necessário que o governo...' }
      ],
    };
  }

  // ==========================================
  // NEW CREDITS-AWARE ANALYSIS METHOD
  // ==========================================

  /**
   * Analyze essay and persist results with credit management
   * This is the new robust method that handles credits, validation, and atomic operations
   */
  async analyzeAndPersist({ essayId, essayText, userId, imageB64 = null, imageUrl = null, examType = 'enem', themeName = null, setProgress = () => {} }) {
    if (!essayId) throw new Error('Essay ID is required');
    if (!userId) throw new Error('User ID is required');
    if (!essayText && !imageB64) throw new Error('Essay text or image is required');

    try {
      // Step 1: Check if user has credits
      setProgress('Verificando créditos...');
      const hasCredits = await essayService.hasUserCredits(userId, 1);
      if (!hasCredits) {
        return {
          success: false,
          reason: 'NO_CREDITS',
          message: 'Você não possui créditos suficientes para análise. Adquira mais créditos para continuar.',
          needsPayment: true
        };
      }

      // Step 2: Call OpenAI API for analysis
      setProgress('Analisando redação com IA...');
      const apiResponse = await this.analyzeEssay(essayText, setProgress, imageB64, imageUrl, examType, themeName);
      
      if (!apiResponse || !apiResponse.analysis) {
        return {
          success: false,
          reason: 'API_ERROR',
          message: 'Erro na comunicação com o serviço de análise. Tente novamente.',
          retryable: true
        };
      }

      // Step 3: Parse and validate the analysis
      setProgress('Processando resultados...');
      let parsedAnalysis;
      try {
        parsedAnalysis = this.parseAnalysisResponse(apiResponse.analysis);
      } catch (e) {
        console.error('Failed to parse analysis response:', e);
        return {
          success: false,
          reason: 'PARSE_ERROR',
          message: 'Erro ao processar a análise. Tente novamente.',
          retryable: true,
          rawResponse: apiResponse.analysis
        };
      }

      if (parsedAnalysis && typeof parsedAnalysis === 'object' && apiResponse?.model) {
        parsedAnalysis.model = apiResponse.model;
      }

      // Validate that we have meaningful analysis data
      if (!parsedAnalysis || !parsedAnalysis.scores) {
        return {
          success: false,
          reason: 'MODEL_OUTPUT_INVALID',
          message: 'A análise retornada está incompleta. Tente novamente.',
          retryable: true,
          rawResponse: parsedAnalysis,
          diagnostic: 'Missing required fields: scores or totalScore'
        };
      }

      // Check if we have valid competency scores (exam-type aware)
      const examCompetencies = {
        enem: ['I', 'II', 'III', 'IV', 'V'],
        fuvest: ['content', 'structure', 'language'],
        unicamp: ['content_and_argumentation', 'genre_adequacy', 'structure_and_cohesion', 'language_and_register'],
        uerj: ['understanding_of_prompt', 'argumentative_quality', 'textual_cohesion', 'formal_language_control']
      };
      
      const competenciesToCheck = examCompetencies[examType] || examCompetencies.enem;
      const hasValidScores = competenciesToCheck.every(comp => {
        const score = parsedAnalysis.scores?.[comp]?.score;
        return typeof score === 'number' && score >= 0;
      });

      if (!hasValidScores) {
        return {
          success: false,
          reason: 'MODEL_OUTPUT_INVALID',
          message: 'A análise não contém notas válidas para os critérios. Tente novamente.',
          retryable: true,
          rawResponse: parsedAnalysis,
          diagnostic: `No valid scores found for exam type: ${examType}. Expected keys: ${competenciesToCheck.join(', ')}`
        };
      }

      // Step 4: Atomic save and credit deduction
      setProgress('Salvando análise e processando crédito...');
      const saveResult = await essayService.updateEssayAnalysisAndSpendCredit({
        userId,
        essayId,
        analysisData: parsedAnalysis,
        computeTotalIfMissing: true,
        imageUrl: imageUrl || null,
        examType
      });

      if (!saveResult.success) {
        // This shouldn't happen if we checked credits above, but handle it
        if (saveResult.error && saveResult.error.includes('NO_CREDITS')) {
          return {
            success: false,
            reason: 'NO_CREDITS',
            message: 'Créditos insuficientes.',
            needsPayment: true
          };
        }
        throw new Error(saveResult.error || 'Failed to save analysis');
      }

      // Step 5: Success!
      setProgress('Análise concluída com sucesso!');
      // Silently delete the blob if we used an image URL
     try {
       if (imageUrl) {
         // Fire-and-forget; do not block UI
         fetch('/api/blob/delete', {
           method: 'POST',
           headers: { 'content-type': 'application/json' },
           body: JSON.stringify({ url: imageUrl })
         }).catch(() => {});
       }
     } catch (_) { /* ignore */ }

      return {
        success: true,
        analysis: saveResult.analysis,
        creditsRemaining: saveResult.creditsRemaining,
        alreadyAnalyzed: saveResult.alreadyAnalyzed,
        usage: apiResponse.usage,
        message: saveResult.alreadyAnalyzed 
          ? 'Esta redação já foi analisada anteriormente.'
          : `Análise concluída! Créditos restantes: ${saveResult.creditsRemaining}`
      };

    } catch (error) {
      console.error('analyzeAndPersist error:', error);
      
      // Handle specific error types
      if (error.message === 'NO_CREDITS') {
        return {
          success: false,
          reason: 'NO_CREDITS',
          message: 'Créditos insuficientes para análise.',
          needsPayment: true
        };
      }

      // Handle network/API errors
      const errorMsg = error.message || 'Erro desconhecido';
      console.log(errorMsg);
      const isRetryable = errorMsg.includes('timeout') || 
                         errorMsg.includes('network') || 
                         errorMsg.includes('502') || 
                         errorMsg.includes('504') ||
                         errorMsg.includes('ECONNRESET');

      return {
        success: false,
        reason: 'UNEXPECTED_ERROR',
        message: isRetryable 
          ? 'Erro de conexão. Tente novamente em alguns instantes.'
          : 'Erro interno. Entre em contato com o suporte se o problema persistir.',
        retryable: isRetryable,
        error: errorMsg
      };
    }
  }
}

function sleep(ms) { 
  return new Promise(r => setTimeout(r, ms)); 
}

const openaiService = new OpenAIService();
export default openaiService;
